import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { IMessage, StompHeaders } from '@stomp/stompjs';
import { WebSocketConnectionService } from './websocket-connection.service';
import { QueueWorkerFacadeService } from './queue-worker-facade.service';
import { AuthService } from '../auth/auth.service';
import { TelemetryService } from '../telemetry/telemetry.service';
import { LoggingService } from '../logging/logging.service';
import { trace, context } from '@opentelemetry/api';

/**
 * Interface for buffered messages that couldn't be sent immediately
 */
interface PendingFrame {
  destination: string;
  body: any;
  expiresAt: number; // epoch-ms timestamp when this message expires
}

/**
 * Service responsible for WebSocket message subscription and sending
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketMessagingService implements OnDestroy {
  // Message and subscription management
  private messageSubjects: { [destination: string]: BehaviorSubject<any> } = {};
  private activeSubscriptions: { [destination: string]: any } = {};
  private pendingSubscriptions: Array<{ destination: string, callback: (message: any) => void }> = [];
  private destroy$ = new Subject<void>();
  
  // Connection status
  private connectedSubject = new BehaviorSubject<boolean>(false);
  public connected$ = this.connectedSubject.asObservable();
  
  // Buffer for outgoing messages when socket isn't connected
  private readonly outgoingBuffer: PendingFrame[] = [];
  private readonly maxBufferedFrames = 200;
  private readonly ttlMsPerFrame = 30000; // 30 seconds

  constructor(
    private connectionService: WebSocketConnectionService,
    private queueService: QueueWorkerFacadeService,
    private authService: AuthService,
    private telemetryService: TelemetryService,
    private logger: LoggingService
  ) {
    // Subscribe to connection events to process pending subscriptions when connected
    this.connectionService.connected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async connected => {
        this.connectedSubject.next(connected);

        if (connected) {
          this.processPendingSubscriptions();
          await this.flushBuffer(); // Flush any buffered messages when connection is established
          this.triggerPendingMessageProcessing(); // Trigger fetch of messages from IndexedDB queue
        } else {
          this.clearSubscriptions();
        }
      });

    // Subscribe to incoming messages from the worker and route to destination-specific subjects
    this.connectionService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(messageData => {
        console.log('[MAIN-THREAD] 📨 Received message from worker:', messageData);
        if (messageData && messageData.destination) {
          const destination = messageData.destination;
          const payload = messageData.message;

          this.logger.debug('WebSocketMessagingService', `Received message from worker for ${destination}`);
          console.log('[MAIN-THREAD] 🎯 Routing to destination:', destination, 'hasSubject:', !!this.messageSubjects[destination]);

          // Route to the appropriate destination subject
          if (this.messageSubjects[destination]) {
            this.messageSubjects[destination].next(payload);
            console.log('[MAIN-THREAD] ✅ Routed message to subject');
          } else {
            this.logger.warn('WebSocketMessagingService', `Received message for unsubscribed destination: ${destination}`);
            console.warn('[MAIN-THREAD] ❌ No subject for destination:', destination);
          }
        }
      });

    // FIX: Subscribe ONCE to pending messages from queue (fixes race condition + subscription accumulation)
    // This subscription lives for the entire service lifetime, preventing:
    // 1. Race condition: Subscription exists before first getPendingMessages() call
    // 2. Subscription accumulation: Only one subscription, not one per reconnection
    this.queueService.pendingMessages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (messages) => {
        // Only process if connected
        if (!this.connectionService.isConnected()) {
          this.logger.debug('WebSocketMessagingService', 'Pending messages received but not connected - ignoring');
          return;
        }

        if (!messages || messages.length === 0) {
          this.logger.info('WebSocketMessagingService', 'No pending messages to process');
          return;
        }

        this.logger.info('WebSocketMessagingService', `Processing ${messages.length} pending messages`);

        for (const message of messages) {
          this.logger.info('WebSocketMessagingService', `Processing pending message ${message.id}`, {
            destination: message.destination,
            conversationId: message.conversationId,
            timestamp: message.timestamp
          });

          try {
            // Parse the message content
            const body = JSON.parse(message.content);
            this.logger.debug('WebSocketMessagingService', `Parsed message body for ${message.id}`, {
              hasContent: !!body,
              bodyType: typeof body
            });

            // Attempt to send
            await this.sendDirect(message.destination, body);
            this.logger.info('WebSocketMessagingService', `✅ Successfully sent pending message ${message.id}`);

            // Mark as sent
            this.queueService.markSent(message.id);
            this.logger.debug('WebSocketMessagingService', `Marked ${message.id} as sent in queue`);
          } catch (error) {
            // Mark as failed (will be retried later)
            this.queueService.markFailed(message.id);

            this.logger.error('WebSocketMessagingService', `❌ Failed to send pending message ${message.id}`, {
              error: String(error),
              stack: error instanceof Error ? error.stack : undefined,
              destination: message.destination
            });
          }
        }

        this.logger.info('WebSocketMessagingService', `Completed processing ${messages.length} pending messages`);
      });
  }

  /**
   * Cleanup resources when service is destroyed
   */
  ngOnDestroy(): void {
    this.logger.info('WebSocketMessagingService', 'Destroying service');
    this.destroy$.next();
    this.destroy$.complete();

    // Cleanup all subscriptions
    this.clearSubscriptions();

    // Clear message subjects
    for (const subject of Object.values(this.messageSubjects)) {
      subject.complete();
    }
    this.messageSubjects = {};
    this.pendingSubscriptions = [];
    
    // Clear message buffer
    this.outgoingBuffer.length = 0;
    
    // Complete the connected subject
    this.connectedSubject.complete();
  }

  /**
   * Process any pending subscriptions after connection
   */
  private processPendingSubscriptions(): void {
    if (!this.connectionService.isConnected()) {
      this.logger.warn('WebSocketMessagingService', `Cannot process pending subscriptions - not connected`);
      return;
    }

    this.logger.info('WebSocketMessagingService', `Processing ${this.pendingSubscriptions.length} pending subscription(s)`);

    // Subscribe to all pending destinations using facade
    while (this.pendingSubscriptions.length > 0) {
      const sub = this.pendingSubscriptions.shift();
      if (sub && !this.activeSubscriptions[sub.destination]) {
        try {
          // Use facade's subscribe method (worker-based)
          this.connectionService.subscribe(sub.destination);

          // Mark as active (worker handles actual subscription)
          this.activeSubscriptions[sub.destination] = { destination: sub.destination };

          this.logger.info('WebSocketMessagingService', `✅ Moved ${sub.destination} from pending to activeSubscriptions`);
        } catch (error) {
          this.logger.error('WebSocketMessagingService', `Error processing pending subscription to ${sub.destination}`, error);
          this.telemetryService.trackError('websocket_pending_subscription_failed', {
            error: String(error),
            destination: sub.destination
          });
        }
      } else if (sub) {
        this.logger.warn('WebSocketMessagingService', `Skipping ${sub.destination} - already in activeSubscriptions`);
      }
    }

    this.logger.info('WebSocketMessagingService', `✅ Pending subscriptions processed, activeSubscriptions count: ${Object.keys(this.activeSubscriptions).length}`);
  }

  /**
   * Subscribe to messages from a specific destination
   * @returns Promise<Observable> of parsed message data (async due to JWT token validation)
   */
  public async subscribe<T>(destination: string): Promise<Observable<T>> {
    // FIN-0079 Phase 2: Detect and recreate stale subscriptions
    // A subscription is stale if messageSubject exists but activeSubscription doesn't
    const hasMessageSubject = !!this.messageSubjects[destination];
    const hasActiveSubscription = !!this.activeSubscriptions[destination];
    const isStale = hasMessageSubject && !hasActiveSubscription;

    if (isStale) {
      this.logger.warn('WebSocketMessagingService', `Stale subscription detected for ${destination}, recreating`);
      // Complete and remove the stale subject
      this.messageSubjects[destination].complete();
      delete this.messageSubjects[destination];
    }

    // Create a subject if it doesn't exist
    if (!this.messageSubjects[destination]) {
      const isCurrentlyConnected = this.connectionService.isConnected();
      this.logger.info('WebSocketMessagingService', `Creating new subscription for destination: ${destination}, isConnected: ${isCurrentlyConnected}`);
      this.messageSubjects[destination] = new BehaviorSubject<T>(null as unknown as T);

      // Create and store the callback for this subscription
      const messageCallback = (payload: any, headers?: StompHeaders) => {
        // Extract trace context from headers if present
        if (headers && headers['traceparent']) {
          try {
            // Parse traceparent header (format: 00-traceId-spanId-flags)
            const [version, traceId, spanId, flags] = headers['traceparent'].split('-');
            if (version === '00' && traceId && spanId) {
              // Store trace context in the payload
              payload._tracingContext = {
                traceId,
                spanId,
                traceFlags: parseInt(flags || '01', 16)
              };
            }
          } catch (e) {
            this.logger.debug('WebSocketMessagingService', 'Error parsing trace context from WebSocket message', e);
          }
        }
        this.messageSubjects[destination].next(payload);
      };

      // If already connected, subscribe immediately using facade
      if (this.connectionService.isConnected() && !this.activeSubscriptions[destination]) {
        try {
          // Use facade's subscribe method (worker handles STOMP subscription)
          this.connectionService.subscribe(destination);

          // Mark as active
          this.activeSubscriptions[destination] = { destination };

          this.logger.info('WebSocketMessagingService', `✅ Subscribed to ${destination} and added to activeSubscriptions`);
        } catch (error) {
          this.logger.error('WebSocketMessagingService', `Error subscribing to ${destination}`, error);
          this.pendingSubscriptions.push({
            destination: destination,
            callback: messageCallback
          });
          this.logger.warn('WebSocketMessagingService', `Added ${destination} to pendingSubscriptions due to error`);
        }
      } else if (!this.activeSubscriptions[destination]) {
        this.pendingSubscriptions.push({
          destination: destination,
          callback: messageCallback
        });
        this.logger.warn('WebSocketMessagingService', `Added ${destination} to pendingSubscriptions (not connected or already active), isConnected: ${this.connectionService.isConnected()}, hasActive: ${!!this.activeSubscriptions[destination]}`);

        // Make sure we're connected
        if (!this.connectionService.isConnected()) {
          this.connectionService.connect().catch(() => {
            // Ignore errors, already logged in connection service
          });
        }
      }
    }

    return this.messageSubjects[destination]
      .asObservable()
      .pipe(filter(message => message !== null));
  }

  /**
   * Send a message to a specific destination.
   *
   * ╔══════════════════════════════════════════════════════════════════════════╗
   * ║  DESIGN BY CONTRACT: This is the SINGLE entry point for sending messages ║
   * ╠══════════════════════════════════════════════════════════════════════════╣
   * ║  1. Messages are ALWAYS queued to IndexedDB FIRST (offline-first)        ║
   * ║  2. If connected: sends immediately, marks as sent                       ║
   * ║  3. If disconnected: stays in queue, sent on reconnect                   ║
   * ║                                                                          ║
   * ║  ⚠️  CALLERS MUST NOT check connection status before calling this!       ║
   * ║      This method handles both connected and disconnected cases.          ║
   * ║      Adding connection checks in upper layers BREAKS offline support.    ║
   * ║                                                                          ║
   * ║  History: FIN-0028 (data loss), FIN-0100 (orphaned messages)             ║
   * ╚══════════════════════════════════════════════════════════════════════════╝
   *
   * @param destination STOMP destination (e.g., '/app/chat')
   * @param body Message payload
   */
  public async send(destination: string, body: any): Promise<void> {
    // Generate unique message ID if not present
    const messageId = body.messageId || `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // IMMEDIATELY enqueue to IndexedDB (prevents FIN-0028 data loss)
    // CRITICAL: This WAITS for DB to be ready (fixes race condition)
    try {
      await this.queueService.enqueue({
        id: messageId,
        content: JSON.stringify(body),
        conversationId: body.conversationId || 'unknown',
        timestamp: Date.now(),
        destination
      });

      this.logger.debug('WebSocketMessagingService', `Message ${messageId} enqueued to IndexedDB`);
    } catch (error) {
      this.logger.error('WebSocketMessagingService', `CRITICAL: Failed to enqueue message ${messageId} to IndexedDB`, error);
      // Still try to send, but message is NOT safe from data loss!
      this.telemetryService.trackError('queue_enqueue_failed', {
        error: String(error),
        messageId
      });
    }

    if (this.connectionService.isConnected()) {
      // Fast path - try to send immediately if connected
      try {
        await this.sendDirect(destination, body);
        // Mark as sent in queue worker
        this.queueService.markSent(messageId);
        this.logger.debug('WebSocketMessagingService', `Message ${messageId} sent and marked as sent`);
        return;
      } catch (error) {
        // Mark as failed in queue worker (will be retried later)
        this.queueService.markFailed(messageId);
        this.logger.warn('WebSocketMessagingService', `Send failed for ${messageId}, marked for retry`, error);
      }
    } else {
      this.logger.debug('WebSocketMessagingService', `Message ${messageId} queued (not connected)`);

      // If we're disconnected, initiate a connection
      if (!this.connectionService.isConnected()) {
        this.connectionService.connect().catch((error) => {
          // Log error but don't throw - message is safely queued
          this.logger.error('WebSocketMessagingService', `Failed to establish connection for ${messageId}`, error);
        });
      }
    }
  }
  
  /**
   * Send a message directly to the WebSocket without buffering
   * @private
   */
  private async sendDirect(destination: string, body: any): Promise<void> {
    if (!this.connectionService.isConnected()) {
      this.logger.error('WebSocketMessagingService', 'WebSocket is not connected. Cannot send message directly.');
      throw new Error('WebSocket is not connected');
    }

    try {
      // Use facade's send method (worker handles STOMP publish)
      // NOTE: Worker facade doesn't currently support custom headers
      // JWT token and sessionId are added by the worker from stored state
      this.connectionService.send(destination, body);

      this.logger.debug('WebSocketMessagingService', `Sent message to ${destination} via worker`);

      // Track event with current trace context
      const span = trace.getActiveSpan();
      if (span) {
        const ctx = span.spanContext();
        this.telemetryService.trackEvent('websocket_message_sent', {
          destination,
          messageType: body ? body.type : 'unknown'
        }, ctx.traceId, ctx.spanId);
      } else {
        this.telemetryService.trackEvent('websocket_message_sent', {
          destination,
          messageType: body ? body.type : 'unknown'
        });
      }
    } catch (error) {
      this.logger.error('WebSocketMessagingService', 'Error sending WebSocket message', error);
      this.telemetryService.trackError('websocket_send_failed', {
        error: String(error),
        destination
      });
      throw error; // Re-throw to allow caller to handle
    }
  }
  
  /**
   * Flush buffered messages
   * Called automatically when connection is established
   */
  private async flushBuffer(): Promise<void> {
    if (!this.connectionService.isConnected() || this.outgoingBuffer.length === 0) {
      return;
    }

    this.logger.info('WebSocketMessagingService', `Flushing ${this.outgoingBuffer.length} buffered messages`);
    const now = Date.now();
    const stillQueued: PendingFrame[] = [];

    for (const frame of this.outgoingBuffer) {
      // Skip expired messages
      if (frame.expiresAt < now) {
        this.logger.debug('WebSocketMessagingService', `Dropping expired message to ${frame.destination}`);
        continue;
      }

      try {
        await this.sendDirect(frame.destination, frame.body);
      } catch (error) {
        // Keep in buffer if send fails
        stillQueued.push(frame);
        this.logger.warn('WebSocketMessagingService', `Failed to flush message to ${frame.destination}, will retry later`, error);
      }
    }

    // Replace buffer with messages that couldn't be sent
    this.outgoingBuffer.length = 0;
    this.outgoingBuffer.push(...stillQueued);
    
    if (stillQueued.length > 0) {
      this.logger.info('WebSocketMessagingService', `${stillQueued.length} messages still in buffer after flush attempt`);
    }
  }

  /**
   * Clear all active subscriptions
   * FIN-0079 Phase 2: Clear messageSubjects to force re-creation on reconnect
   */
  private clearSubscriptions(): void {
    this.logger.info('WebSocketMessagingService', `Clearing ${Object.keys(this.activeSubscriptions).length} active subscriptions`);

    // Unsubscribe from all active subscriptions using facade
    Object.entries(this.activeSubscriptions).forEach(([destination, _]) => {
      try {
        // Use facade's unsubscribe method (worker handles STOMP unsubscription)
        this.connectionService.unsubscribe(destination);
        this.logger.debug('WebSocketMessagingService', `Unsubscribed from ${destination} via worker`);
      } catch (e) {
        this.logger.error('WebSocketMessagingService', `Error unsubscribing from ${destination}`, e);
      }
    });

    // Clear subscription maps
    this.activeSubscriptions = {};

    // FIN-0079 Phase 2: Clear messageSubjects to prevent stale subscriptions
    // Complete all subjects before clearing to clean up RxJS subscriptions
    for (const subject of Object.values(this.messageSubjects)) {
      subject.complete();
    }
    this.messageSubjects = {};

    // Clear pending subscriptions
    this.pendingSubscriptions = [];

    this.logger.info('WebSocketMessagingService', 'All subscriptions cleared, ready for reconnection');
  }

  /**
   * FIN-0079 Phase 2 (Option C): Verify subscription health
   * Checks if a subscription exists and is properly configured
   * @param destination The subscription destination to check
   * @returns true if subscription is healthy, false otherwise
   */
  public verifySubscriptionHealth(destination: string): boolean {
    const hasMessageSubject = !!this.messageSubjects[destination];
    const hasActiveSubscription = !!this.activeSubscriptions[destination];
    const isConnected = this.connectionService.isConnected();

    if (!isConnected) {
      this.logger.debug('WebSocketMessagingService', `Health check for ${destination}: Not connected`);
      return false;
    }

    if (hasMessageSubject && !hasActiveSubscription) {
      this.logger.warn('WebSocketMessagingService', `Health check FAILED for ${destination}: Stale subscription detected (has subject but no active subscription)`);
      return false;
    }

    if (!hasMessageSubject && hasActiveSubscription) {
      this.logger.warn('WebSocketMessagingService', `Health check FAILED for ${destination}: Orphaned subscription detected (has active subscription but no subject)`);
      return false;
    }

    if (!hasMessageSubject && !hasActiveSubscription) {
      this.logger.debug('WebSocketMessagingService', `Health check for ${destination}: No subscription exists`);
      return false;
    }

    this.logger.debug('WebSocketMessagingService', `Health check PASSED for ${destination}: Subscription is healthy`);
    return true;
  }

  /**
   * Trigger fetch of pending messages from IndexedDB queue
   * Called when WebSocket connection is established
   *
   * NOTE: This only triggers the fetch - the actual subscription and processing
   * happens in the constructor (fixes race condition + subscription accumulation)
   */
  private triggerPendingMessageProcessing(): void {
    if (!this.connectionService.isConnected()) {
      this.logger.debug('WebSocketMessagingService', 'Cannot process queued messages - not connected');
      return;
    }

    this.logger.info('WebSocketMessagingService', 'Triggering fetch of pending queued messages from IndexedDB');

    // Request pending messages from queue worker
    // The subscription in the constructor will handle the response
    this.queueService.getPendingMessages();
  }
}